import { v4 as uuidv4 } from 'uuid'
import { createSelector } from '@reduxjs/toolkit'

import { SourceFilters, selectValidSpatialFilter, selectViewport } from '@carto/react-redux'
import { Layer } from '@carto/kepler.gl/layers'
import { normalizeGeometry, getGeometryToIntersect } from '@carto/react-core'
import { getTable } from '@carto/react-widgets'
import { MultiPolygon, Polygon } from 'geojson'
import Papa from 'papaparse'

import { AppStore, RootState, runAppSaga } from 'app/state/store'
import { LocalLayerDataExportJob, ExportJob, ExportDataMethod, JobStatusType, JobType } from 'features/jobs/types'
import { addExportJob, updateExportJob } from 'features/jobs/state/jobsExportingSlice'
import { isApiError } from 'features/common/api/apiError'
import { Connection, ProviderType } from 'features/connections'
import { MapType } from 'features/maps/types'
import { KeplerLayer, MapDatasetEnriched } from '../types'
import { getEnrichedDatasetById } from '../state/keplerGlSlice'
import { exportDatasetsRemote } from 'features/jobs/models/exportsModel'
import { setLocalExportData } from './localExportsStore'
import { waitAllTilesLoaded } from '../state/datasetSagas'
import { getSpatialFiltersOptions } from '../state/builderSliceUtils'
import { waitUntilSelectorEqual } from 'features/common/state/sagaUtils'

export const MAX_EXPORTABLE_LAYERS = 8

const NEEDS_SPATIAL_DATA_OPTIONS_FOR_WKT = [ProviderType.REDSHIFT, ProviderType.POSTGRES]

/** Reasons that a layer is NOT allowed or supported for export. */
type UnexportableLayerReason = 'inactive-in-legend' | 'unsupported-export-method' | 'agent-layer'

/**
 * Reasons visible (e.g. as tooltips) to end-users. For all other reasons, the
 * layer is simply hidden from the list.
 */
export const VISIBLE_UNEXPORTABLE_LAYER_REASONS: UnexportableLayerReason[] = ['unsupported-export-method']

type ExportableLayerInfo = {
  id: string
  name: string
  method?: ExportDataMethod
  dataset: MapDatasetEnriched
  unexportableReason?: UnexportableLayerReason
}

export const getExportableLayersInfo: (state: RootState) => ExportableLayerInfo[] = createSelector(
  (state: RootState) => state.maps.currentMapDatasets,
  (state: RootState) => state.keplerGl.map?.visState.layers,
  (state: RootState) => state.connections.myConnections,
  (state: RootState) => state.builder.legendSettings,
  (state: RootState) => state.builder.genAiThread?.layers,
  (datasets, layers, connections, legendSettings, agentLayers) => {
    const agentLayerIds = new Set(agentLayers?.map((l) => l.layerId))
    return (
      layers
        ?.map((layer) => {
          const datasetId = layer.config.dataId
          const dataset = datasets.find((d) => d.id === datasetId) as MapDatasetEnriched
          const isLayerInactiveInLegend = legendSettings?.layers[layer.id]?.active === false
          const isAgentLayer = agentLayerIds.has(layer.id)
          const exportMethod = getDatasetExportMethod(dataset, connections)

          const getUnexportableReason = (): UnexportableLayerReason | undefined => {
            if (isLayerInactiveInLegend) return 'inactive-in-legend'
            if (isAgentLayer) return 'agent-layer'
            if (!exportMethod) return 'unsupported-export-method'
            return undefined
          }

          const unexportableReason = getUnexportableReason()
          const isExportable = !unexportableReason

          return {
            name: layer.config.label as string,
            method: isExportable ? exportMethod : undefined,
            dataset,
            unexportableReason
          }
        })
        .filter(Boolean) || []
    )
  }
)

export const hasExportableLayers = createSelector(getExportableLayersInfo, (info) =>
  info ? info.some(({ method }) => method !== undefined) : false
)

function getDatasetExportMethod(dataset: MapDatasetEnriched, connections?: Connection[]): ExportDataMethod | undefined {
  if (dataset.type === MapType.tileset || dataset.type === MapType.raster) return undefined
  if (dataset.exportToBucketAvailable) return ExportDataMethod.ServerExport

  const connection = connections?.find((c) => dataset.connectionName === c.name || dataset.connectionId === c.id)

  return connection?.exportToBucketAvailable ? ExportDataMethod.ServerExport : undefined
}

export async function exportLayersData({
  store,
  layerNames: _layerNames
}: {
  store: AppStore
  layerNames?: string[]
}): Promise<void> {
  const initialState = store.getState()
  const { geomFilter, filters: filtersByDatasetId } = getCurrentViewFilters(initialState)

  const connections = initialState.connections.myConnections
  const allLayers: Layer[] = initialState.keplerGl.map.visState.layers
  const layerNames = _layerNames || allLayers.map((layer) => layer.config.label as string)

  const remoteExportJobs: ExportJob[] = []
  const localExports: LocalLayerDataExportJob[] = []
  for (const layerName of layerNames) {
    const layer = allLayers.find((layer) => layer.config.label === layerName)
    if (!layerName) {
      throw new Error(`exportLayersData: layer not found`)
    }
    const datasetId = (layer as KeplerLayer).config.dataId
    const dataset = getEnrichedDatasetById(initialState, datasetId)

    if (!dataset || !dataset.connectionName) {
      throw new Error(`exportLayersData: dataset invalid or not found`)
    }

    const method = getDatasetExportMethod(dataset, connections)

    if (!method) {
      throw new Error(`Layer '${layerName}' cannot be exported`)
    }

    if (method === ExportDataMethod.Local) {
      const localJob: LocalLayerDataExportJob = {
        type: JobType.LocalLayerDataExport,
        createdAt: Date.now(),
        id: uuidv4(),
        status: JobStatusType.Success,
        name: layerName,
        params: {
          datasetId,
          filters: filtersByDatasetId[datasetId]
        }
      }
      localExports.push(localJob)
      store.dispatch(addExportJob(localJob))
    } else {
      const { spatialDataColumn, spatialDataType } = getSpatialFiltersOptions(dataset)

      const spatialDataOptions =
        NEEDS_SPATIAL_DATA_OPTIONS_FOR_WKT.includes(dataset.providerId) && spatialDataColumn
          ? {
              column: spatialDataColumn,
              type: spatialDataType
            }
          : undefined
      const remoteJob: ExportJob = {
        type: JobType.DatasetExport,
        name: layerName,
        id: uuidv4(),
        createdAt: Date.now(),
        status: JobStatusType.Pending,
        params: {
          name: layerName,
          type: dataset.type,
          source: dataset.source,
          connectionName: dataset.connectionName,
          queryParameters: dataset.queryParameters ? dataset.queryParameters : undefined,
          filters: filtersByDatasetId[datasetId],
          spatialFilter: spatialDataColumn && geomFilter ? { [spatialDataColumn]: geomFilter } : undefined,
          spatialDataType,
          spatialDataOptions,
          accessToken: dataset.accessToken
        }
      }
      remoteExportJobs.push(remoteJob)
      store.dispatch(addExportJob(remoteJob))
    }
  }

  await Promise.all([
    createRemoteExportJobs({ jobs: remoteExportJobs, store }),
    peformLocalExports({ jobs: localExports, store })
  ])
}

async function createRemoteExportJobs({ jobs, store }: { jobs: ExportJob[]; store: AppStore }) {
  const remoteResults = await exportDatasetsRemote({
    sources: jobs.map((job) => job.params)
  })

  for (const result of remoteResults) {
    if (isApiError(result)) {
      jobs.forEach((job) => {
        if (job.params?.connectionName === result.connectionName) {
          store.dispatch(
            updateExportJob({
              id: job.id,
              status: JobStatusType.Failure,
              error: { msg: `Failed to create job: ${result.error}`, code: String(result.status) }
            })
          )
        }
      })
    } else {
      const { name, ...jobParams } = result
      const job = jobs.find((task) => task.name === name)
      if (!job) continue

      jobParams.accessToken = job.params.accessToken
      store.dispatch(updateExportJob({ id: job.id, status: JobStatusType.Pending, jobParams }))
    }
  }
}

async function peformLocalExports({ jobs, store }: { jobs: LocalLayerDataExportJob[]; store: AppStore }) {
  return Promise.all(
    jobs.map(async (job) => {
      store.dispatch(updateExportJob({ id: job.id, status: JobStatusType.Pending }))
      try {
        const data = await exportLayerDataLocal({ store, ...job.params })
        const dataAsCsv = Papa.unparse(data)

        setLocalExportData(job.id, dataAsCsv)

        store.dispatch(updateExportJob({ id: job.id, status: JobStatusType.Success }))
      } catch (error) {
        console.error('peformLocalExports', error)
        store.dispatch(
          updateExportJob({ id: job.id, status: JobStatusType.Failure, error: { msg: String(error), code: '500' } })
        )
      }
    })
  )
}

function getCurrentViewFilters(state: RootState): {
  filters: Record<string, SourceFilters>
  geomFilter: Polygon | MultiPolygon | null
} {
  const filters = Object.entries(state.carto.dataSources).reduce((agg, [dataSourceId, cartoDataSourceState]) => {
    agg[dataSourceId] = cartoDataSourceState.filters || {}
    return agg
  }, {})

  // TODO: this is partly logic from useWidgetFetch/selectGeometryToIntersect, so maybe export from c4r (?)
  const spatialFilter = selectValidSpatialFilter(state)
  const viewport = selectViewport(state)
  const geomFilter = normalizeGeometry(getGeometryToIntersect(viewport, spatialFilter ? spatialFilter.geometry : null))

  return { filters, geomFilter }
}

export async function exportLayerDataLocal({
  store,
  datasetId,
  filters
}: {
  store: AppStore
  datasetId: string
  filters?: SourceFilters
}): Promise<Record<string, unknown>[]> {
  const initialState = store.getState()

  const cartoSource = initialState.carto.dataSources[datasetId]

  if (!cartoSource) {
    throw new Error(`exportLayerDataLocal: dataset invalid or not found`)
  }

  const opts = {
    source: { ...cartoSource, filters: filters || cartoSource.filters },
    rowsPerPage: 0
  }

  await runAppSaga(function* () {
    yield waitAllTilesLoaded(datasetId)
    yield waitUntilSelectorEqual((state) => state.builder.tilesetWidgetSourceReady?.[datasetId], true)
  })

  const tableResponse: { rows: Record<string, unknown>[] } = await getTable(opts)
  if (!tableResponse?.rows) {
    throw new Error('Invalid response from server: no rows')
  }
  return tableResponse?.rows
}
